import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw, Phone, MessageCircle, Inbox } from "lucide-react";
import { api, API, apiError } from "../lib/api";
import { toWaPhone, waLink } from "../lib/contact";

const STATUS_LABELS = { nou: "Nou", in_lucru: "În lucru", finalizat: "Finalizat" };
const STATUS_STYLES = {
  nou: "bg-oak/15 text-walnut border-oak/40",
  in_lucru: "bg-blue-50 text-blue-800 border-blue-200",
  finalizat: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("toate");
  const [loadingList, setLoadingList] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/quote-requests");
      setRequests(data);
    } catch (err) {
      toast.error(apiError(err, "Nu am putut încărca cererile."));
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setUser(data);
        } catch {
          setUser(false);
        }
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) loadRequests();
  }, [user, loadRequests]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Bine ai revenit!");
    } catch (err) {
      setError(apiError(err, "Autentificare eșuată."));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quote-requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status actualizat.");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (checking) {
    return (
      <main className="grid min-h-screen place-items-center bg-cream pt-20" data-testid="admin-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay">Se verifică sesiunea...</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="grid min-h-screen place-items-center bg-anthracite px-5 pt-20" data-testid="admin-login-page">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-cream/10 bg-coal p-8 sm:p-10"
        >
          <img src="/assets/logo-light.png" alt="LOGITEH PROD · Mobilă din viitor" className="h-10 w-auto" />
          <h1 className="mt-6 font-serif text-3xl font-bold text-cream">Administrare cereri</h1>
          <p className="mt-2 text-sm text-cream/50">Zonă rezervată echipei LOGITEH PROD.</p>
          <div className="mt-8 space-y-5">
            <div>
              <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-oak">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-sm border border-cream/15 bg-anthracite px-4 py-3.5 text-sm text-cream outline-none transition-colors focus:border-oak"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-oak">
                Parolă
              </label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-sm border border-cream/15 bg-anthracite px-4 py-3.5 text-sm text-cream outline-none transition-colors focus:border-oak"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="h-13 w-full rounded-full bg-oak py-4 text-xs font-bold uppercase tracking-[0.2em] text-anthracite transition-colors hover:bg-cream"
            >
              Autentificare
            </button>
          </div>
        </form>
      </main>
    );
  }

  const visible = filter === "toate" ? requests : requests.filter((r) => r.status === filter);

  return (
    <main className="min-h-screen bg-cream pb-20 pt-28" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-walnut">Panou administrare</p>
            <h1 className="mt-2 font-serif text-3xl font-bold text-ink">Cereri de ofertă</h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadRequests}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-xs font-semibold text-ink transition-colors hover:border-oak"
            >
              <RefreshCw size={14} className={loadingList ? "animate-spin" : ""} /> Reîmprospătează
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full bg-anthracite px-5 py-2.5 text-xs font-semibold text-cream transition-colors hover:bg-walnut"
            >
              <LogOut size={14} /> Ieșire
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-status-filters">
          {["toate", "nou", "in_lucru", "finalizat"].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`rounded-full border px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                filter === s ? "border-anthracite bg-anthracite text-cream" : "border-line bg-white text-clay hover:border-oak"
              }`}
            >
              {s === "toate" ? "Toate" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <div className="mt-10 flex flex-col items-center gap-4 border border-dashed border-line bg-white py-20 text-center" data-testid="admin-empty-state">
            <Inbox size={32} className="text-clay" />
            <p className="text-sm text-clay">Nicio cerere în această categorie momentan.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4" data-testid="admin-requests-list">
            {visible.map((r) => (
              <article key={r.id} data-testid={`admin-request-${r.id}`} className="border border-line bg-white p-6">
                <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="font-serif text-xl font-bold text-ink" data-testid={`request-name-${r.id}`}>
                        {r.name}
                      </h2>
                      <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[r.status] || STATUS_STYLES.nou}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-walnut">{r.project_type}</p>
                    {r.message && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-clay">{r.message}</p>}
                    <p className="mt-3 text-xs text-clay/70">
                      {new Date(r.created_at).toLocaleString("ro-RO")}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <a
                        href={`tel:${r.phone.replace(/\s/g, "")}`}
                        data-testid={`request-call-${r.id}`}
                        className="flex items-center gap-2 rounded-full border border-line px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-oak"
                      >
                        <Phone size={13} /> {r.phone}
                      </a>
                      <a
                        href={waLink(`Bună ziua, ${r.name}! Vă contactăm de la LOGITEH PROD referitor la cererea de ofertă: ${r.project_type}.`).replace("40745419700", toWaPhone(r.phone))}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`request-whatsapp-${r.id}`}
                        className="flex items-center gap-2 rounded-full border border-[#25D366]/40 px-4 py-2 text-xs font-semibold text-[#128C7E] transition-colors hover:bg-[#25D366] hover:text-white"
                      >
                        <MessageCircle size={13} /> Răspunde pe WhatsApp
                      </a>
                      {r.attachment_path && (
                        <a
                          href={`${API}/files/${r.attachment_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`request-attachment-${r.id}`}
                          className="flex items-center gap-3 rounded-full border border-line px-3 py-1.5 text-xs text-clay transition-colors hover:border-oak"
                        >
                          <img
                            src={`${API}/files/${r.attachment_path}`}
                            alt={r.attachment_name || "Schiță atașată"}
                            className="h-8 w-8 rounded-sm object-cover"
                          />
                          Vezi schița
                        </a>
                      )}
                    </div>
                  </div>
                  <select
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    data-testid={`request-status-select-${r.id}`}
                    className="rounded-sm border border-line bg-cream px-4 py-2.5 text-xs font-semibold text-ink outline-none focus:border-oak"
                  >
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
